import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Breadcrumb, Tabs, Spin, message, Modal } from "antd";
import axios from "axios";
import { BASE_URL } from "../../Constant";
import {
  addAddressApi,
  deleteAddressApi,
  geAddressApi,
  updateAddressApi,
} from "../../Features/Address/Address";
import { logout } from "../../Features/User/User";
import { getOrderVariantTags } from "../../utils/cartVariants";
import styles from "./AccountPortal.module.scss";

/* ────────────────────────────────────────────────────────── */
/*  Helpers                                                    */
/* ────────────────────────────────────────────────────────── */

const formatDate = (raw) => {
  if (!raw) return "N/A";
  const d = new Date(raw);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (val) => {
  if (val == null) return "N/A";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(val);
};

const statusClass = (status) => {
  const map = {
    Pending: styles.statusPending,
    Confirmed: styles.statusConfirmed,
    Dispatched: styles.statusDispatched,
    Delivered: styles.statusDelivered,
    Cancelled: styles.statusCancelled,
  };
  return map[status] || styles.statusDefault;
};

const getTierClass = (tier) => {
  const t = (tier || "").toLowerCase();
  if (t.includes("platinum")) return styles.tierPlatinum;
  if (t.includes("gold")) return styles.tierGold;
  if (t.includes("silver")) return styles.tierSilver;
  return styles.tierStandard;
};

const tierBenefits = {
  Standard: [
    "Access to full product range",
    "Standard delivery options",
    "Email support",
    "Monthly newsletters",
  ],
  Silver: [
    "Access to full product range",
    "Priority delivery",
    "Email & phone support",
    "Monthly newsletters",
    "5% loyalty discount",
    "Early access to new arrivals",
  ],
  Gold: [
    "Access to full product range",
    "Express delivery included",
    "Dedicated account manager",
    "Weekly market updates",
    "10% loyalty discount",
    "Early access to new arrivals",
    "Exclusive collections",
    "Priority CAD services",
  ],
  Platinum: [
    "Access to full product range",
    "Same-day dispatch",
    "Dedicated account manager",
    "Real-time market alerts",
    "15% loyalty discount",
    "First access to new arrivals",
    "Exclusive & limited collections",
    "Priority CAD services",
    "Bespoke design consultations",
    "Invite-only events",
  ],
};

const COUNTRIES = [
  "United Kingdom","Ireland","United States","Canada","Australia",
  "Afghanistan","Albania","Algeria","Andorra","Angola","Argentina","Armenia","Austria","Azerbaijan",
  "Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Bermuda","Bolivia",
  "Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Cambodia","Cameroon","Chile",
  "China","Colombia","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark",
  "Dominican Republic","Ecuador","Egypt","Estonia","Ethiopia","Fiji","Finland","France","Georgia",
  "Germany","Ghana","Greece","Guatemala","Hong Kong","Hungary","Iceland","India","Indonesia","Iran",
  "Iraq","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kuwait","Latvia","Lebanon",
  "Libya","Lithuania","Luxembourg","Malaysia","Maldives","Malta","Mauritius","Mexico","Monaco",
  "Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Nepal","Netherlands","New Zealand",
  "Nigeria","North Macedonia","Norway","Oman","Pakistan","Panama","Paraguay","Peru","Philippines",
  "Poland","Portugal","Qatar","Romania","Russia","Saudi Arabia","Scotland","Senegal","Serbia",
  "Singapore","Slovakia","Slovenia","South Africa","South Korea","Spain","Sri Lanka","Sweden",
  "Switzerland","Taiwan","Tanzania","Thailand","Trinidad and Tobago","Tunisia","Turkey","Uganda",
  "Ukraine","United Arab Emirates","Uruguay","Venezuela","Vietnam","Wales","Zambia","Zimbabwe",
];

/* ────────────────────────────────────────────────────────── */
/*  Icons (inline SVGs)                                        */
/* ────────────────────────────────────────────────────────── */

const IconOrders = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);

const IconBrowse = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const IconGold = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const IconContact = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconEmpty = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);

const IconClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

/* ────────────────────────────────────────────────────────── */
/*  Component                                                  */
/* ────────────────────────────────────────────────────────── */

function AccountPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userToken = useSelector((state) => state.user?.token);
  const token = userToken || localStorage.getItem("token");
  const addressList = useSelector((state) => state.address?.addressData) || [];
  const addressLoading = useSelector((state) => state.address?.isAddressLoading);

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(false);

  // Address modal state
  const [addrModalOpen, setAddrModalOpen] = useState(false);
  const [addrEdit, setAddrEdit] = useState(null); // null = add, object = editing
  const [addrForm, setAddrForm] = useState({ name: "", phone: "", address1: "", address2: "", city: "", postcode: "", country: "United Kingdom", type: "" });
  const [addrLookupResults, setAddrLookupResults] = useState([]);
  const [addrLookupOpen, setAddrLookupOpen] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobileNumber: "",
    companyName: "",
  });

  /* ── Fetch Data ──────────────────────────────────────────── */

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!token) {
      setLoading(false);
      return;
    }
    fetchAll();
    dispatch(geAddressApi(token));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const headers = { Authorization: token };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [profileRes, settingsRes, ordersRes] = await Promise.allSettled([
        axios.get(`${BASE_URL}/user/profile/get`, { headers }),
        axios.get(`${BASE_URL}/user/my-settings`, { headers }),
        axios.get(`${BASE_URL}/order/getAll`, { headers }),
      ]);

      if (profileRes.status === "fulfilled") {
        const p = profileRes.value?.data?.user || profileRes.value?.data?.data || profileRes.value?.data;
        setProfile(p);
        setForm({
          name: p?.User_Name || p?.name || "",
          email: p?.User_Email || p?.email || "",
          mobileNumber: (() => {
            const num = String(p?.User_Mobile_No || p?.mobileNumber || "");
            if (!num) return "";
            if (num.startsWith("0") || num.startsWith("+")) return num;
            return "0" + num;
          })(),
          companyName: p?.Company || p?.companyName || "",
        });
      }

      if (settingsRes.status === "fulfilled") {
        const s = settingsRes.value?.data?.data || settingsRes.value?.data;
        setSettings(s);
      }

      if (ordersRes.status === "fulfilled") {
        const raw = ordersRes.value?.data;
        const o = raw?.orderList || raw?.data || raw || [];
        setOrders(Array.isArray(o) ? o : []);
      }
    } catch (err) {
      console.error("Account portal fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Update Profile ──────────────────────────────────────── */

  const handleSave = async () => {
    if (!form.name.trim()) {
      message.warning("Please enter your name");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("mobileNumber", form.mobileNumber);

      await axios.patch(`${BASE_URL}/user/profile/update`, fd, { headers });
      message.success("Profile updated successfully");
      fetchAll();
    } catch (err) {
      message.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  /* ── Address Helpers ─────────────────────────────────────── */

  const openAddAddress = () => {
    setAddrEdit(null);
    setAddrForm({ name: "", phone: "", address1: "", address2: "", city: "", postcode: "", country: "United Kingdom", type: "" });
    setAddrLookupResults([]);
    setAddrLookupOpen(false);
    setAddrModalOpen(true);
  };

  const openEditAddress = (item) => {
    setAddrEdit(item);
    setAddrForm({
      name: item?.Name || "",
      phone: item?.Phone_Number || "",
      address1: item?.House || "",
      address2: item?.Full_Address || "",
      city: item?.City || "",
      postcode: item?.Pincode ? String(item.Pincode) : "",
      country: item?.State || "United Kingdom",
      type: item?.Type || "",
    });
    setAddrLookupResults([]);
    setAddrLookupOpen(false);
    setAddrModalOpen(true);
  };

  const handleAddrSave = () => {
    if (!addrForm.name.trim()) { message.warning("Enter a name"); return; }
    if (!addrForm.address1.trim()) { message.warning("Enter address line 1"); return; }
    if (!addrForm.city.trim()) { message.warning("Enter a city"); return; }
    if (!addrForm.type) { message.warning("Select address type"); return; }
    const payload = {
      Type: addrForm.type,
      Phone_Number: addrForm.phone,
      Name: addrForm.name,
      landmark: "-",
      House: addrForm.address1,
      Full_Address: addrForm.address2 || "-",
      City: addrForm.city,
      State: addrForm.country || "United Kingdom",
      Pincode: addrForm.postcode?.trim() || "",
    };
    const onDone = () => {
      dispatch(geAddressApi(token));
      setAddrModalOpen(false);
      setAddrEdit(null);
    };
    if (addrEdit) dispatch(updateAddressApi(payload, token, addrEdit._id, onDone));
    else dispatch(addAddressApi(payload, token, onDone));
  };

  const handleAddrDelete = (id) => {
    dispatch(deleteAddressApi(id, token));
  };

  const handlePostcodeLookup = async () => {
    if (!addrForm.postcode?.trim()) { message.error("Enter a postcode"); return; }
    try {
      const res = await axios.get(`https://api.postcodes.io/postcodes/${encodeURIComponent(addrForm.postcode.trim())}`);
      const r = res?.data?.result;
      if (r) {
        const city = r.ttwa || r.admin_district || r.parish || "";
        const district = r.admin_district || "";
        const country = r.country || "United Kingdom";
        const suggestions = [];
        if (r.admin_ward) suggestions.push({ label: `${r.admin_ward}, ${district}, ${city}`, address2: r.admin_ward, city, country });
        if (r.bua && r.bua !== r.admin_ward) suggestions.push({ label: `${r.bua}, ${district}, ${city}`, address2: r.bua, city, country });
        if (district && district !== city) suggestions.push({ label: `${district}, ${city}`, address2: district, city, country });
        if (suggestions.length === 0) suggestions.push({ label: `${city}, ${country}`, address2: "", city, country });
        setAddrLookupResults(suggestions);
        setAddrLookupOpen(true);
      }
    } catch { message.error("Postcode not found"); }
  };

  /* ── Derived Data ────────────────────────────────────────── */

  const userName = form.name || "";
  const accountTier = settings?.accountTier || settings?.tier || profile?.tier || "Standard";
  const paymentTerms = settings?.paymentTerms || settings?.Payment_Terms || "Standard";
  const creditLimit = settings?.creditLimit || settings?.Credit_Limit;
  const businessType = settings?.businessType || settings?.Business_Type || profile?.businessType || "Retailer";
  const memberSince = profile?.createdAt || profile?.Created_At || profile?.created_at;
  const visibility = settings?.visibility || settings?.Visibility || {};
  const termsAccepted = settings?.termsAccepted ?? settings?.Terms_Accepted ?? true;
  const benefits = tierBenefits[accountTier] || tierBenefits.Standard;

  /* ── Navigate to Order ───────────────────────────────────── */

  const viewOrder = (record) => {
    navigate("/orderview", { state: record });
  };

  /* ── Tab: Dashboard ──────────────────────────────────────── */

  const DashboardTab = () => (
    <div>
      {/* Stat Cards */}
      <div className={styles.statGrid}>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Total Orders</p>
          <p className={styles.statValue}>{orders.length}</p>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Account Tier</p>
          <div>
            <span className={`${styles.tierBadge} ${getTierClass(accountTier)}`}>
              {accountTier}
            </span>
          </div>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Payment Terms</p>
          <p className={styles.statValue} style={{ fontSize: 16 }}>{paymentTerms}</p>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Member Since</p>
          <p className={styles.statValue} style={{ fontSize: 16 }}>{formatDate(memberSince)}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        <p className={styles.sectionLabel}>Quick Actions</p>
        <div className={styles.actionGrid}>
          <Link to="/order" className={styles.actionBtn}>
            <IconOrders /> View Orders
          </Link>
          <Link to="/product/all" className={styles.actionBtn}>
            <IconBrowse /> Browse Products
          </Link>
          <Link to="/sell-gold/get-a-quote" className={styles.actionBtn}>
            <IconGold /> Sell Your Gold
          </Link>
          <Link to="/contact" className={styles.actionBtn}>
            <IconContact /> Contact Us
          </Link>
        </div>
      </div>

      {/* Tier Benefits */}
      <div className={styles.benefitsCard}>
        <h3 className={styles.benefitsTitle}>
          Your {accountTier} Tier Benefits
        </h3>
        <ul className={styles.benefitsList}>
          {benefits.map((b, i) => (
            <li key={i} className={styles.benefitItem}>
              <IconCheck /> {b}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  /* ── Tab: My Details ─────────────────────────────────────── */

  const s = settings?.settings || settings || {};

  const DetailsTab = () => (
    <div className={styles.detailsLayout}>
      {/* Business Details */}
      <div className={styles.formCard}>
        <h3 className={styles.formTitle} style={{ marginBottom: 18 }}>Business Details <span style={{ fontSize: 9, color: "#bbb", fontWeight: 400, letterSpacing: 0 }}>— managed by your account manager</span></h3>
        <div className={styles.formGrid}>
          {[
            { label: "Contact Name", value: form.name || s.User_Name || s.Contact_Person },
            { label: "Registered Business Name", value: form.companyName || s.Company },
            { label: "Trading Name", value: s.Trading_Name },
            { label: "Email Address", value: form.email || s.User_Email },
            { label: "Mobile Number", value: form.mobileNumber || (() => { const n = String(s.User_Mobile_No || ""); if (!n) return ""; if (n.startsWith("0") || n.startsWith("+")) return n; return "0" + n; })() },
            { label: "Landline", value: s.Landline },
            { label: "Secondary Email", value: s.Secondary_Email },
            { label: "Website", value: s.Website },
            { label: "Business Type", value: s.Business_Type },
            { label: "Company Reg. Number", value: s.Company_Reg_Number },
            { label: "VAT Number", value: s.VAT_Number },
          ].map((f, i) => (
            <div key={i} className={styles.formGroup}>
              <label className={styles.formLabel}>{f.label}</label>
              <div className={styles.formReadonly}>{f.value || "—"}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Registered Address */}
      <div className={styles.formCard}>
        <h3 className={styles.formTitle} style={{ marginBottom: 18 }}>Registered Address</h3>
        <div className={styles.formGrid}>
          {[
            { label: "Address Line 1", value: s.Address_Line_1 },
            { label: "Address Line 2", value: s.Address_Line_2 },
            { label: "City", value: s.City },
            { label: "Postcode", value: s.Postcode },
            { label: "Country", value: s.Country },
          ].map((f, i) => (
            <div key={i} className={styles.formGroup}>
              <label className={styles.formLabel}>{f.label}</label>
              <div className={styles.formReadonly}>{f.value || "—"}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11, color: "#bbb", marginTop: 14 }}>To update your details, please contact your account manager.</p>
      </div>
    </div>
  );

  /* ── Tab: My Addresses ──────────────────────────────────── */

  const AddressesTab = () => (
    <div>
      <div className={styles.formCard}>
        <div className={styles.addrHeader}>
          <h3 className={styles.formTitle} style={{ margin: 0, padding: 0, border: "none" }}>Delivery Addresses</h3>
          <button type="button" className={styles.addrAddBtn} onClick={openAddAddress}>+ Add Address</button>
        </div>

        {addressList.length === 0 ? (
          <div className={styles.addrEmpty}>No saved addresses. Add one to get started.</div>
        ) : (
          <div className={styles.addrList}>
            {addressList.map((item, i) => (
              <div key={item?._id || i} className={styles.addrCard}>
                <div className={styles.addrCardContent}>
                  <div className={styles.addrCardTop}>
                    <span className={styles.addrTypeBadge}>{item?.Type || "Other"}</span>
                    <span className={styles.addrCardName}>{item?.Name}</span>
                    {item?.Phone_Number && <span className={styles.addrCardPhone}>{item.Phone_Number}</span>}
                  </div>
                  <p className={styles.addrCardText}>
                    {item?.House}{item?.Full_Address && item.Full_Address !== "-" ? `, ${item.Full_Address}` : ""}, {item?.City}{item?.Pincode ? `, ${item.Pincode}` : ""}{item?.State ? `, ${item.State}` : ""}
                  </p>
                </div>
                <div className={styles.addrCardActions}>
                  <button type="button" className={styles.addrIconBtn} onClick={() => openEditAddress(item)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                  </button>
                  <button type="button" className={`${styles.addrIconBtn} ${styles.addrIconBtnDanger}`} onClick={() => handleAddrDelete(item._id)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#cc004c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  /* ── Tab: My Orders ──────────────────────────────────────── */

  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("All");
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const statusFilters = ["All", "Pending", "Confirmed", "Dispatched", "Delivered", "Cancelled"];

  const filteredOrders = orders.filter((o) => {
    const matchesStatus = orderStatusFilter === "All" || o?.Status === orderStatusFilter;
    const matchesSearch = !orderSearch.trim() || (o?.orderId || "").toLowerCase().includes(orderSearch.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getItemCount = (order) =>
    order?.cartData?.length || order?.orderItems?.length || order?.OrderItems?.length || order?.items?.length || 0;

  const getItemThumbs = (order) =>
    (order?.cartData || order?.orderItems || order?.OrderItems || order?.items || []).slice(0, 3);

  const OrdersTab = () => {
    if (!orders.length) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><IconEmpty /></div>
          <p className={styles.emptyTitle}>No Orders Yet</p>
          <p className={styles.emptyDesc}>You haven't placed any orders yet. Browse our collection to find something exceptional.</p>
          <Link to="/product/all" className={styles.emptyBtn}>Browse Products</Link>
        </div>
      );
    }

    return (
      <div>
        {/* Search + Filter bar */}
        <div className={styles.ordersToolbar}>
          <div className={styles.ordersSearchWrap}>
            <svg className={styles.ordersSearchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input className={styles.ordersSearchInput} value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} placeholder="Search by order ID..." />
          </div>
          <div className={styles.ordersFilters}>
            {statusFilters.map((s) => (
              <button key={s} type="button" className={`${styles.ordersFilterBtn} ${orderStatusFilter === s ? styles.ordersFilterBtnActive : ""}`} onClick={() => setOrderStatusFilter(s)}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <p className={styles.ordersCount}>{filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""}</p>

        {filteredOrders.length === 0 ? (
          <div className={styles.addrEmpty}>No orders match your filters.</div>
        ) : (
          <div className={styles.ordersCards}>
            {filteredOrders.map((order, idx) => {
              const id = order?._id || idx;
              const isExpanded = expandedOrderId === id;
              const items = order?.cartData || order?.orderItems || order?.OrderItems || order?.items || [];
              const thumbs = getItemThumbs(order);
              const itemCount = getItemCount(order);

              return (
                <div key={id} className={styles.orderCard}>
                  {/* Main row */}
                  <div className={styles.orderCardMain} onClick={() => setExpandedOrderId(isExpanded ? null : id)}>
                    {/* Thumbnails */}
                    <div className={styles.orderThumbs}>
                      {thumbs.map((item, i) => (
                        <div key={i} className={styles.orderThumb}>
                          <img src={item?.productImage || item?.Variation?.image || item?.Product?.images?.[0]} alt="" />
                        </div>
                      ))}
                      {itemCount > 3 && <div className={styles.orderThumbMore}>+{itemCount - 3}</div>}
                    </div>

                    {/* Info */}
                    <div className={styles.orderCardInfo}>
                      <div className={styles.orderCardTop}>
                        <span className={styles.orderCardId}>{order?.orderId || order?.OrderId || `#${idx + 1}`}</span>
                        <span className={`${styles.statusBadge} ${statusClass(order?.Status)}`}>{order?.Status || "Processing"}</span>
                      </div>
                      <div className={styles.orderCardMeta}>
                        <span>{formatDate(order?.createdAt || order?.Created_At)}</span>
                        <span>{itemCount} {itemCount === 1 ? "item" : "items"}</span>
                        <span className={styles.orderCardPrice}>{formatCurrency(order?.FinalPrice || order?.finalPrice)}</span>
                      </div>
                    </div>

                    {/* Expand arrow */}
                    <div className={styles.orderCardArrow} style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className={styles.orderCardExpanded}>
                      {/* Items list */}
                      <div className={styles.orderItemsList}>
                        {items.map((item, i) => {
                          const tags = getOrderVariantTags(item);

                          return (
                            <div key={i} className={styles.orderItem}>
                              <div className={styles.orderItemImg}>
                                <img src={item?.productImage || item?.Variation?.image || item?.Product?.images?.[0]} alt="" />
                              </div>
                              <div className={styles.orderItemBody}>
                                <div className={styles.orderItemHeader}>
                                  <p className={styles.orderItemName}>{item?.productName || item?.Product?.product_Name || "Product"}</p>
                                  <span className={styles.orderItemPrice}>{formatCurrency(item?.totalPrice || item?.total || item?.price)}</span>
                                </div>
                                {tags.length > 0 && (
                                  <div className={styles.orderItemTags}>
                                    {tags.map(([label, value], j) => (
                                      <span key={j} className={styles.orderItemTag}>
                                        <span className={styles.orderItemTagLabel}>{label}:</span> {value}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <div className={styles.orderItemFooter}>
                                  <span className={styles.orderItemQty}>Qty: {item?.quantity || item?.Quantity || 1}</span>
                                  <span className={styles.orderItemUnit}>{formatCurrency(item?.unitPrice)} each</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Summary */}
                      <div className={styles.orderSummary}>
                        <div className={styles.orderSummaryRow}>
                          <span>Subtotal</span>
                          <span>{formatCurrency(order?.OriginalPrice || order?.originalPrice)}</span>
                        </div>
                        {Number(order?.Shipping_Charge) > 0 && (
                          <div className={styles.orderSummaryRow}>
                            <span>Shipping</span>
                            <span>{formatCurrency(order?.Shipping_Charge)}</span>
                          </div>
                        )}
                        {Number(order?.CouponPrice) > 0 && (
                          <div className={`${styles.orderSummaryRow} ${styles.orderSummaryDiscount}`}>
                            <span>Coupon Discount</span>
                            <span>-{formatCurrency(order?.CouponPrice)}</span>
                          </div>
                        )}
                        {Number(order?.VAT_Amount) > 0 && (
                          <div className={styles.orderSummaryRow}>
                            <span>VAT ({order?.VAT_Rate || 20}%)</span>
                            <span>{formatCurrency(order?.VAT_Amount)}</span>
                          </div>
                        )}
                        <div className={`${styles.orderSummaryRow} ${styles.orderSummaryTotal}`}>
                          <span>Total{Number(order?.VAT_Amount) > 0 ? " (inc. VAT)" : ""}</span>
                          <span>{formatCurrency(order?.FinalPrice || order?.finalPrice)}</span>
                        </div>
                      </div>

                      {/* Cancellation reason */}
                      {(order?.Status === "Cancelled" || order?.OrderType === "6") && order?.reason && (
                        <div style={{ padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", fontSize: 12, color: "#991b1b", marginBottom: 14 }}>
                          <strong>Cancellation reason:</strong> {order.reason}
                        </div>
                      )}

                      {/* Actions */}
                      <div className={styles.orderCardActions}>
                        <button type="button" className={styles.orderViewBtn} onClick={() => viewOrder(order)}>View Full Details</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  /* ── Tab: Settings ───────────────────────────────────────── */

  const SettingsTab = () => {
    const visibleMenus = visibility?.menus || visibility?.Menus || [];
    const visibleBrands = visibility?.brands || visibility?.Brands || [];

    return (
      <div>
        {/* Account Info */}
        <div className={styles.formCard}>
          <h3 className={styles.formTitle}>Account Settings</h3>
          <div className={styles.settingsGrid}>
            <div className={styles.settingsItem}>
              <span className={styles.settingsLabel}>Account Tier</span>
              <span className={styles.settingsValue}>{accountTier}</span>
            </div>
            {creditLimit != null && (
              <div className={styles.settingsItem}>
                <span className={styles.settingsLabel}>Credit Limit</span>
                <span className={styles.settingsValue}>{formatCurrency(creditLimit)}</span>
              </div>
            )}
            <div className={styles.settingsItem}>
              <span className={styles.settingsLabel}>Payment Terms</span>
              <span className={styles.settingsValue}>{paymentTerms}</span>
            </div>
          </div>
        </div>

        {/* Visibility */}
        {(visibleMenus.length > 0 || visibleBrands.length > 0) && (
          <div className={styles.formCard}>
            <h3 className={styles.formTitle}>Your Visibility</h3>

            {visibleMenus.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <p className={styles.sectionLabel}>Available Menus</p>
                <div className={styles.visibilityList}>
                  {visibleMenus.map((m, i) => (
                    <span key={i} className={styles.visibilityTag}>
                      {typeof m === "string" ? m : m?.name || m?.Name || "Menu"}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {visibleBrands.length > 0 && (
              <div>
                <p className={styles.sectionLabel}>Available Brands</p>
                <div className={styles.visibilityList}>
                  {visibleBrands.map((b, i) => (
                    <span key={i} className={styles.visibilityTag}>
                      {typeof b === "string" ? b : b?.name || b?.Brand_Name || "Brand"}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Terms */}
        <div className={styles.formCard}>
          <h3 className={styles.formTitle}>Terms & Conditions</h3>
          <div className={`${styles.termsStatus} ${termsAccepted ? styles.termsAccepted : styles.termsPending}`}>
            {termsAccepted ? <IconCheck /> : <IconClock />}
            <span>
              {termsAccepted
                ? "Terms & Conditions accepted"
                : "Terms & Conditions pending acceptance"}
            </span>
          </div>
        </div>

        {/* Logout */}
        <button type="button" onClick={() => { dispatch(logout()); navigate("/"); }} className={styles.logoutBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
          Log Out
        </button>
      </div>
    );
  };

  /* ── Tab Config ──────────────────────────────────────────── */

  const tabItems = [
    { key: "dashboard", label: <span>Dashboard</span>, children: DashboardTab() },
    { key: "details", label: <span>My<br/>Details</span>, children: DetailsTab() },
    { key: "addresses", label: <span>My<br/>Addresses</span>, children: AddressesTab() },
    { key: "orders", label: <span>My<br/>Orders</span>, children: OrdersTab() },
    { key: "settings", label: <span>Settings</span>, children: SettingsTab() },
  ];

  /* ── Render ──────────────────────────────────────────────── */

  return (
    <div className={styles.page}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumbBar}>
        <div className={styles.breadcrumbInner}>
          <Breadcrumb className={styles.breadcrumb}>
            <Breadcrumb.Item>
              <Link to="/">Home</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>My Account</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      {/* Page Title */}
      <div className={styles.pageTitle}>
        <span className={styles.titleEyebrow}>The Diamond Counter</span>
        <h1 className={styles.titleText}>My Account</h1>
        <div className={styles.titleLine} />
      </div>

      {/* Content */}
      <div className={styles.container}>
        {loading ? (
          <div className={styles.loadingWrap}>
            <Spin size="large" />
          </div>
        ) : !token ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <p className={styles.emptyTitle}>Sign In Required</p>
            <p className={styles.emptyDesc}>
              Please sign in to access your account portal and manage your retailer dashboard.
            </p>
            <Link to="/signin" className={styles.emptyBtn}>
              Sign In
            </Link>
          </div>
        ) : (
          <>
            <div className={styles.welcome}>
              <h2 className={styles.welcomeText}>Welcome back{userName ? `, ${userName}` : ""}</h2>
              <p className={styles.welcomeSub}>
                Manage your account, track orders, and access your benefits from your personal dashboard.
              </p>
            </div>
            <Tabs
              className={styles.tabBar}
              defaultActiveKey={location.state?.tab || "dashboard"}
              items={tabItems}
            />
          </>
        )}
      </div>

      {/* Address Add/Edit Modal */}
      <Modal open={addrModalOpen} onCancel={() => setAddrModalOpen(false)} footer={null} title={null} centered width={440} zIndex={10001} className={styles.themedModal}>
        <h3 className={styles.modalTitle}>{addrEdit ? "Edit Address" : "Add New Address"}</h3>
        <div className={styles.modalBody}>
          <div className={styles.modalRow}>
            <div className={styles.modalField}>
              <label className={styles.formLabel}>Full Name</label>
              <input className={styles.formInput} value={addrForm.name} onChange={(e) => setAddrForm({ ...addrForm, name: e.target.value })} placeholder="Full name" />
            </div>
            <div className={styles.modalField}>
              <label className={styles.formLabel}>Phone Number</label>
              <input className={styles.formInput} value={addrForm.phone} onChange={(e) => setAddrForm({ ...addrForm, phone: e.target.value })} placeholder="+44 7XXX XXXXXX" />
            </div>
          </div>

          <div className={styles.modalField} style={{ position: "relative" }}>
            <label className={styles.formLabel}>Postcode Lookup</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input className={styles.formInput} style={{ flex: 1 }} value={addrForm.postcode} onChange={(e) => { setAddrForm({ ...addrForm, postcode: e.target.value.toUpperCase() }); setAddrLookupOpen(false); }} placeholder="e.g. M1 1AA" />
              <button type="button" onClick={handlePostcodeLookup} className={styles.addrFindBtn}>Find</button>
            </div>
            {addrLookupOpen && addrLookupResults.length > 0 && (
              <div className={styles.addrLookupDropdown}>
                <div className={styles.addrLookupHeader}>Select area</div>
                {addrLookupResults.map((addr, i) => (
                  <div key={i} className={styles.addrLookupItem} onClick={() => {
                    setAddrForm(prev => ({ ...prev, address2: addr.address2, city: addr.city, country: addr.country }));
                    setAddrLookupOpen(false);
                  }}>
                    {addr.label}
                  </div>
                ))}
                <div className={styles.addrLookupManual} onClick={() => {
                  const first = addrLookupResults[0];
                  setAddrForm(prev => ({ ...prev, city: first.city, country: first.country }));
                  setAddrLookupOpen(false);
                }}>
                  Enter manually
                </div>
              </div>
            )}
          </div>

          <div className={styles.modalField}>
            <label className={styles.formLabel}>Address Line 1</label>
            <input className={styles.formInput} value={addrForm.address1} onChange={(e) => setAddrForm({ ...addrForm, address1: e.target.value })} placeholder="Apartment, suite, unit, etc." />
          </div>
          <div className={styles.modalField}>
            <label className={styles.formLabel}>Address Line 2</label>
            <input className={styles.formInput} value={addrForm.address2} onChange={(e) => setAddrForm({ ...addrForm, address2: e.target.value })} placeholder="Area, district, neighbourhood" />
          </div>
          <div className={styles.modalRow}>
            <div className={styles.modalField}>
              <label className={styles.formLabel}>City</label>
              <input className={styles.formInput} value={addrForm.city} onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} placeholder="City" />
            </div>
            <div className={styles.modalField}>
              <label className={styles.formLabel}>Country</label>
              <select className={styles.formInput} style={{ cursor: "pointer" }} value={addrForm.country} onChange={(e) => setAddrForm({ ...addrForm, country: e.target.value })}>
                <option value="">Select country</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={styles.formLabel} style={{ marginBottom: 6, display: "block" }}>Address Type</label>
            <div className={styles.addrTypeRow}>
              {["Home", "Work", "Other"].map((type) => (
                <button key={type} type="button" onClick={() => setAddrForm({ ...addrForm, type })}
                  className={`${styles.addrTypeBtn} ${addrForm.type === type ? styles.addrTypeBtnActive : ""}`}>
                  {type}
                </button>
              ))}
            </div>
          </div>

          <button type="button" onClick={handleAddrSave} disabled={addressLoading} className={styles.addrSaveBtn}>
            {addressLoading ? "Saving..." : addrEdit ? "Update Address" : "Save Address"}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default AccountPortal;
